import { useQuery } from 'react-query';

import type { ILeadLabel } from 'src/services/api/leads/types';
import type { IPropertyLabel } from 'src/services/api/properties/labels/types';
import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { LabelType, type ILabel } from 'src/services/api/labels/types';

export const GET_LABELS_PATH = 'api/landlord/labels';

type ILabelsByType = Partial<Record<LabelType, ILabel[]>>;

export type IGetLabelsResponse = {
    labels: ILabelsByType;
};

const fetchLabels = async () => {
    const res = await apiClient(GET_LABELS_PATH).json<IGetLabelsResponse>();
    return res.labels;
};

/**
 * Every label lives in a single query, and the per-type hooks below are
 * `select` views onto it. One request, one cache entry, so a label rename
 * refreshes every consumer at once.
 */
const useLabels = <T,>(select: (labels: ILabelsByType) => T) => {
    return useQuery(queryKeys.labels, fetchLabels, { select });
};

// Module-level so react-query sees a stable identity and does not recompute on every render.
const selectLeadLabels = (labels: ILabelsByType) => (labels[LabelType.LEAD] || []) as ILeadLabel[];
const selectPropertyLabels = (labels: ILabelsByType) => (labels[LabelType.PROPERTY] || []) as IPropertyLabel[];
const selectLabelsById = (labels: ILabelsByType) => new Map(
    Object.values(labels).flat().map((label) => [label.id, label])
);

export const useLeadLabels = () => useLabels(selectLeadLabels);

export const usePropertyLabels = () => useLabels(selectPropertyLabels);

export type ILabelsById = Map<number, ILabel>;

// Stable identity so consumers memoizing on the lookup do not churn while it loads.
const EMPTY_LABELS_BY_ID: ILabelsById = new Map();

/**
 * Lookup of every label by id, for resolving the label ids carried on leads
 * and properties. Entities embed a snapshot of their labels; that snapshot
 * goes stale on rename, so read the name/color from here instead.
 */
export const useLabelsById = (): ILabelsById => useLabels(selectLabelsById).data || EMPTY_LABELS_BY_ID;

export const resolveLabels = <T extends { id: number }>(labels: T[], labelsById: ILabelsById): T[] => {
    return labels.map((label) => (labelsById.get(label.id) as T | undefined) || label);
};

export const resolveLabelNames = (labels: { id: number, name?: string }[], labelsById: ILabelsById): string[] => {
    // Falls back to the embedded name so search and filtering still work before the catalog loads.
    return labels.map((label) => labelsById.get(label.id)?.name || label.name).filter((name): name is string => Boolean(name));
};
